import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { EditField } from "./EditField";
import { EditFieldGrid } from "./EditFieldGrid";
import { buildGroupHierarchy, annotateVisibilityAndFilter, sortFieldsByAppearanceOrder, shouldShowField } from "../utils/grouping";
import { updateFlattenedNestedValue } from "../utils/nestedValueUtils";
import { useEdit } from "../context/EditContext";
import { useEditUI } from "../context/EditUIPreferencesContext";
import { getIcon } from "../utils/icons";
import InternalElementsList from "./InternalElementsList";
import { typography, surfaces, borders, textColors } from "../../design-system/tokens";
import { ChevronDownIcon, ChevronUpIcon } from "../../design-system/icons";
import useMatchMedia from "../../core/hooks/useMatchMedia";
import ElementStylePresetsTab from "./panels/ElementStylePresetsTab";
import { SUPPORTED_ELEMENT_STYLE_PRESET_TYPES } from "./panels/elementStylePresetsApi";

const arraysEqual = (a, b) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

/**
 * In folders view, some nested editors flatten certain background fields (pattern/colors)
 * into `background_pattern` while still being conceptually part of the `background` editor.
 *
 * This helper updates the appropriate nested structure immutably and avoids allocating
 * when the value is unchanged.
 *
 * Exported for unit tests.
 */
const applyFlattenedNestedChange = (
  parentValues,
  parentField,
  nestedFieldName,
  value,
  nestedMetadata,
) => {
  const base = parentValues && typeof parentValues === "object" ? parentValues : null;

  // Special-case: background pattern editor persists under `background_pattern`.
  if (parentField === "background" && nestedFieldName === "pattern" && typeof value === "string") {
    const existingBgPattern = base?.background_pattern || null;
    if (existingBgPattern?.pattern === value) return base || parentValues;
    const nextBgPattern = { ...(existingBgPattern || {}), pattern: value };
    if (!base) return { background_pattern: { pattern: value } };
    return { ...base, background_pattern: nextBgPattern };
  }

  if (parentField === "background" && nestedFieldName === "colors" && Array.isArray(value)) {
    const existingBgPattern = base?.background_pattern || null;
    const existingColors = existingBgPattern?.colors || null;
    if (Array.isArray(existingColors) && arraysEqual(existingColors, value)) return base || parentValues;
    const nextBgPattern = { ...(existingBgPattern || {}), colors: value };
    if (!base) return { background_pattern: { colors: value } };
    return { ...base, background_pattern: nextBgPattern };
  }

  // Default: update the nested object tree by field name.
  return updateFlattenedNestedValue(base, nestedFieldName, value);
};

/**
 * EditTabsWithFolders - Renders edit panel with tabs on top and folder-style
 * group visualization inside each tab. Groups are shown open by default with
 * vertical lines indicating hierarchy.
 * 
 * This component provides a "Folders" view mode where:
 * - Top-level tabs organize content (Basic, groups, Internal Elements)
 * - Groups are always visible (no collapse functionality)
 * - Vertical lines show hierarchy and indentation
 * - Nested groups have distinct backgrounds for clarity
 * 
 * @component
 */

/**
 * TabBar - Renders horizontal tabs with scroll arrows if needed
 * 
 * Features:
 * - Horizontal scrolling for many tabs
 * - Left/right arrows appear when overflow
 * - Active tab highlighting
 * - Icon support for tabs
 * 
 * @param {Array} tabs - Array of tab objects with {key, label, icon}
 * @param {number} activeIndex - Currently active tab index
 * @param {Function} onSelect - Tab selection handler
 */
const TabBar = ({ tabs, activeIndex, onSelect }) => {
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  // Check if scrolling is needed and update arrow visibility
  const updateArrowVisibility = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const hasOverflow = scrollWidth > clientWidth;
    
    setShowLeftArrow(hasOverflow && scrollLeft > 5);
    setShowRightArrow(hasOverflow && scrollLeft < scrollWidth - clientWidth - 5);
  }, []);

  // Update on mount, tab changes, and window resize
  useEffect(() => {
    updateArrowVisibility();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', updateArrowVisibility);
    }
    window.addEventListener('resize', updateArrowVisibility);
    
    return () => {
      if (container) {
        container.removeEventListener('scroll', updateArrowVisibility);
      }
      window.removeEventListener('resize', updateArrowVisibility);
    };
  }, [updateArrowVisibility, tabs]);

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    const scrollAmount = direction === 'left' ? -200 : 200;
    container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  };

  return (
    <div className="relative">
      {/* Left arrow */}
      {showLeftArrow && (
        <button
          onClick={() => scroll('left')}
          className={`absolute left-0 top-0 bottom-0 z-10 px-2 bg-gradient-to-r from-white dark:from-dark-900 to-transparent ${surfaces.hover} transition-colors`}
          style={{ boxShadow: '2px 0 4px rgba(0,0,0,0.05)', WebkitBoxShadow: '2px 0 4px rgba(0,0,0,0.05)' }}
        >
          <svg className={`w-4 h-4 ${textColors.secondary}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}
      
      {/* Tabs container */}
      <div 
        ref={scrollContainerRef}
        className={`flex overflow-x-auto border-b ${borders.default} bg-transparent pt-0 scrollbar-hide`}
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {tabs.map((t, idx) => (
          <button
            key={t.key}
            onClick={() => onSelect(idx)}
            className={`px-3 py-1 whitespace-nowrap -mb-px border-b-2 ${typography.bodySmall} transition-colors ${
              activeIndex === idx
                ? "border-blue-600 dark:border-blue-400 text-blue-700 dark:text-blue-400 font-medium"
                : `border-transparent ${textColors.tertiary} hover:${textColors.primary} hover:border-gray-300 dark:hover:border-gray-600`
            }`}
          >
            <span className="inline-flex items-center gap-2">
              {t.icon && (
                <span className={textColors.tertiary}>{getIcon(t.icon)}</span>
              )}
              <span>{t.label}</span>
            </span>
          </button>
        ))}
      </div>

      {/* Right arrow */}
      {showRightArrow && (
        <button
          onClick={() => scroll('right')}
          className={`absolute right-0 top-0 bottom-0 z-10 px-2 bg-gradient-to-l from-white dark:from-dark-900 to-transparent ${surfaces.hover} transition-colors`}
          style={{ boxShadow: '-2px 0 4px rgba(0,0,0,0.05)', WebkitBoxShadow: '-2px 0 4px rgba(0,0,0,0.05)' }}
        >
          <svg className={`w-4 h-4 ${textColors.secondary}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}
    </div>
  );
};

/**
 * MainFieldsList - Renders fields without any decoration
 * 
 * Used for:
 * - Basic tab fields (main_fields from metadata)
 * - Root-level fields within a group tab
 * 
 * @param {Array} fields - Array of field objects with {field, metadata}
 * @param {Object} values - Current element values
 * @param {Function} onChange - Field change handler
 */
const MainFieldsList = ({ fields, values, onChange }) => {
  const hasVisible = useMemo(
    () => sortFieldsByAppearanceOrder(fields).some(({ metadata }) => shouldShowField(metadata, values)),
    [fields, values]
  );

  if (!hasVisible) return null;

  return (
    <EditFieldGrid
      className="pb-4"
      fields={fields}
      values={values}
      onChange={onChange}
    />
  );
};

/**
 * GroupFolder - Renders a single group with folder-style visualization
 * 
 * Features:
 * - Vertical line starts below the title (not from top)
 * - Indentation increases with nesting level
 * - Nested groups get distinct background color
 * - Proper spacing between fields and nested groups
 * - Dynamic line height adjusts to content
 * 
 * @param {Object} node - The group node with fields and children
 * @param {Object} values - Current element values
 * @param {Function} onChange - Field change handler
 * @param {number} level - Nesting level (0 = root)
 */
const GroupFolder = ({ node, values, onChange, level = 0 }) => {
  const titleRef = useRef(null);
  const [titleHeight, setTitleHeight] = useState(40); // Default estimate
  const [isOpen, setIsOpen] = useState(true);
  // Sticky functionality removed - see GitHub issue: folders_view_sticky_titles_positioning.md
  
  // Measure title height for accurate line positioning
  useEffect(() => {
    if (titleRef.current) {
      setTitleHeight(titleRef.current.offsetHeight);
    }
  }, []);

  const visible = useMemo(
    () => sortFieldsByAppearanceOrder(node.fields).filter(({ metadata }) => shouldShowField(metadata, values)),
    [node.fields, values]
  );

  const hasChildren = node.children && node.children.length > 0;
  const hasFields = visible.length > 0;
  
  // Don't render if no content
  if (!hasFields && !hasChildren) return null;

  const groupTitle = node.groupInfo?.title || node.name;
  const groupIcon = node.groupInfo?.icon;

  // Calculate indentation based on level
  // Level 0 = no left padding (first layer shows fields directly)
  // Each additional level adds 8px for a compact hierarchy
  const leftPadding = level * 8;
  // Content inside the group sits a bit to the right of the line for clarity
  const contentOffset = 6;
  // Line should align under the group's icon center: content start + 8px
  const lineLeftPosition = leftPadding + contentOffset + 8;

  // Only show title for nested levels (level > 0), not for first layer
  const showTitle = level > 0;
  
  return (
    <div 
      className="relative mb-4" 
      style={{ paddingLeft: `${leftPadding}px` }}
    >
      {/* Vertical line for visual hierarchy - ONLY for nested levels (level > 0) */}
      {(hasFields || hasChildren) && level > 0 && isOpen && (
        <div 
          className="absolute w-0.5 bg-gray-300 dark:bg-gray-600"
          style={{ 
            left: `${lineLeftPosition}px`, // Align with icon center for this level
            top: showTitle ? `${titleHeight + 4}px` : 0, // Line starts below the title border
            bottom: 0
          }}
        />
      )}
      
      {/* Group container - add a bit of offset so content is spaced from the line */}
      <div className="relative" style={{ paddingLeft: `${contentOffset}px` }}>
        {/* Group title - ONLY for nested levels (level > 0) */}
        {showTitle && (
          <div 
            ref={titleRef}
            className={`flex items-center gap-2 mb-3 pb-2 border-b ${borders.default} ${typography.bodySmall} ${textColors.secondary} font-medium`}
          >
            {groupIcon && (
              <span className={`${textColors.tertiary} flex-shrink-0`}>
                {getIcon(groupIcon)}
              </span>
            )}
            <span>{groupTitle}</span>
            <button
              type="button"
              aria-label={isOpen ? "Collapse group" : "Expand group"}
              className={`ml-auto p-1 ${textColors.tertiary} hover:${textColors.primary}`}
              onClick={() => setIsOpen((v) => !v)}
            >
              {isOpen ? <ChevronUpIcon size="sm" /> : <ChevronDownIcon size="sm" />}
            </button>
          </div>
        )}
        
        {/* Fields in this group */}
        {hasFields && isOpen && (
          <div className={`space-y-3 ${level > 0 ? 'mb-4' : 'mb-2'}`}>
            {visible.map(({ field, metadata }) => {
              // SPECIAL: If this is a NESTED field, render it AS A FOLDER itself,
              // and its inner groups as sub-folders. This gives the nested object its
              // own title/line and proper hierarchy.
              if (metadata.appearance === "nested" && metadata.nested_fields) {
                // Group nested fields by their group
                const nestedGrouped = Object.entries(metadata.nested_fields || {}).reduce(
                  (groups, [nestedField, nestedMetadata]) => {
                    const groupInfo = nestedMetadata.group || {};
                    const groupName = groupInfo.name || "other";
                    if (!groups[groupName]) {
                      groups[groupName] = {
                        groupInfo: groupInfo,
                        fields: [],
                      };
                    }
                    groups[groupName].fields.push({
                      field: nestedField,
                      metadata: nestedMetadata,
                    });
                    return groups;
                  },
                  {},
                );

                const nestedValues = values[field] || {};

                // FLATTEN nested groups for display (ignore father_group) so siblings are same level
                const flattened = Object.entries(nestedGrouped)
                  .filter(([gName]) => gName !== 'main')
                  .map(([gName, gData]) => ({
                    name: gName,
                    groupInfo: gData.groupInfo || {},
                    fields: gData.fields || [],
                    children: [],
                  }))
                  .sort((a, b) => (a.groupInfo?.order ?? 999) - (b.groupInfo?.order ?? 999));
                const filteredNested = annotateVisibilityAndFilter(flattened, nestedValues);

                // Render the nested groups directly at the current level (no wrapper folder for
                // the nested field itself) when any of the explicit Option D primitives requests
                // it. ``flatten_in_folders`` is the deprecated alias kept for backward compat
                // with metadata that hasn't been migrated yet.
                const shouldRenderInline = Boolean(
                  metadata?.hide_relation_title || metadata?.inline_children || metadata?.flatten_in_folders,
                );
                if (shouldRenderInline) {
                  // Render main fields first (if any) without wrapper
                  const mainFields = nestedGrouped['main']?.fields || [];
                  
                  return (
                    <React.Fragment key={field}>
                      {/* Render main fields inline with proper padding to clear the line */}
                      {mainFields.length > 0 && mainFields
                        .filter(({ metadata: nestedMetadata }) => shouldShowField(nestedMetadata, nestedValues))
                        .map(({ field: nestedField, metadata: nestedMetadata }) => {
                          // eslint-disable-next-line no-console
                          console.log("[FOLDERS_PATTERN_FLOW] Rendering nested field in flatten_in_folders", {
                            parentField: field,
                            nestedField,
                            nestedMetadata: nestedMetadata?.appearance,
                          });
                          // GENERIC NESTED CONTAINER HANDLING:
                          // If the nested field itself is a nested container (appearance === "nested"),
                          // render its children via GroupFolder so inner fields (e.g., pattern, colors, character style fields)
                          // wire correctly and update only that nested object rather than clobbering parent structures.
                          if (nestedMetadata?.appearance === "nested") {
                            const npGrouped = Object.entries(nestedMetadata.nested_fields || {}).reduce(
                              (groups, [nf, nmeta]) => {
                                const ginfo = nmeta.group || {};
                                const gname = ginfo.name || "other";
                                if (!groups[gname]) {
                                  groups[gname] = { groupInfo: ginfo, fields: [] };
                                }
                                groups[gname].fields.push({ field: nf, metadata: nmeta });
                                return groups;
                              },
                              {},
                            );
                            const npMainFields = npGrouped['main']?.fields || [];
                            const npChildren = Object.entries(npGrouped)
                              .filter(([gName]) => gName !== 'main')
                              .map(([gName, gData]) => ({
                                name: gName,
                                groupInfo: gData.groupInfo || {},
                                fields: gData.fields || [],
                                children: [],
                              }))
                              .sort((a, b) => (a.groupInfo?.order ?? 999) - (b.groupInfo?.order ?? 999));
                            const parentNestedNode = {
                              name: nestedField,
                              groupInfo: { title: nestedMetadata.appearance_title || nestedField },
                              fields: npMainFields,
                              children: npChildren,
                            };
                            return (
                              <GroupFolder
                                key={`${field}_${nestedField}`}
                                node={parentNestedNode}
                                values={nestedValues[nestedField] || {}}
                                onChange={(innerFieldName, innerValue) => {
                                  // eslint-disable-next-line no-console
                                  console.log("[FOLDERS_PATTERN_FLOW] GroupFolder(nested) inner onChange", {
                                    innerFieldName,
                                    innerValue,
                                    innerValueType: typeof innerValue,
                                  });
                                  let nextNested = { ...(nestedValues || {}) };
                                  nextNested[nestedField] = {
                                    ...(nestedValues[nestedField] || {}),
                                    [innerFieldName]: innerValue,
                                  };
                                  onChange(field, nextNested);
                                }}
                                level={level + 1}
                              />
                            );
                          }
                          return (
                          <div key={nestedField} className={level > 0 ? 'pl-6' : ''}>
                            <EditField
                              field={nestedField}
                              value={nestedValues[nestedField]}
                              metadata={nestedMetadata}
                              allValues={nestedValues}
                              parentFieldPath={field}
                              onChange={(nestedFieldName, value) => {
                                const nextNested = applyFlattenedNestedChange(
                                  nestedValues,
                                  field,
                                  nestedFieldName,
                                  value,
                                  nestedMetadata,
                                );
                                onChange(field, nextNested);
                              }}
                            />
                          </div>
                          );
                        })}
                      
                      {/* Render nested groups one level deeper to show hierarchy */}
                      {filteredNested.map((childNode) => (
                        <GroupFolder
                          key={`${field}_${childNode.name}`}
                          node={childNode}
                          values={nestedValues}
                          onChange={(nestedFieldName, value) => {
                            const nextNested = applyFlattenedNestedChange(
                              nestedValues,
                              field,
                              nestedFieldName,
                              value,
                              null,
                            );
                            onChange(field, nextNested);
                          }}
                          level={level + 1}
                        />
                      ))}
                    </React.Fragment>
                  );
                }

                // Original behavior: wrap in a folder
                const parentNestedNode = {
                  name: field,
                  groupInfo: { title: metadata.appearance_title || field },
                  fields: (nestedGrouped['main']?.fields) || [],
                  children: filteredNested,
                };

                return (
                  <GroupFolder
                    key={field}
                    node={parentNestedNode}
                    values={nestedValues}
                    onChange={(nestedFieldName, value) => {
                      const nextNested = applyFlattenedNestedChange(
                        nestedValues,
                        field,
                        nestedFieldName,
                        value,
                        metadata,
                      );
                      onChange(field, nextNested);
                    }}
                    level={level + 1}
                  />
                );
              }
              
                  // Regular field - render with padding to clear the vertical line
                  return (
                    <div key={field} className={level > 0 ? 'pl-6' : ''}>
                      <EditField
                        field={field}
                        value={values[field]}
                        metadata={metadata}
                        allValues={values}
                        onChange={onChange}
                      />
                    </div>
                  );
            })}
          </div>
        )}
        
        {/* Nested groups - recursively render with increased level */}
        {hasChildren && isOpen && (
          <div className="space-y-2 mt-2">
            {node.children.map((childNode) => (
              <GroupFolder
                key={childNode.name}
                node={childNode}
                values={values}
                onChange={onChange}
                level={level + 1}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * EditTabsWithFolders - Main component for folders view mode
 * 
 * Renders an edit panel with:
 * - Horizontal tabs at the top (Basic, group tabs, Internal Elements)
 * - Folder-style groups with vertical lines and indentation
 * - All groups shown open (no collapse functionality)
 * - Support for nested groups with visual hierarchy
 * - Conditional field visibility (show_if/hide_if)
 * - Internal elements management
 * 
 * @param {Object} editMetadata - Edit metadata from backend
 * @param {Object} values - Current element values
 * @param {Function} onChange - Field change handler
 * @param {Function} onEditInternalItem - Handler for editing internal elements
 * @param {Function} onDeleteInternalItem - Handler for deleting internal elements
 */
const EditTabsWithFolders = ({ editMetadata, values, onChange, onEditInternalItem, onDeleteInternalItem }) => {
  const { selectedElement } = useEdit();
  const { selectedTabByType, setSelectedTabPathForType } = useEditUI();
  const isMobileViewport = useMatchMedia("(max-width: 767px)", false);

  const elementType = selectedElement?.type || "_unknown_";
  const persistedPath = selectedTabByType[elementType]?.path || [];
  const supportsStylePresets = SUPPORTED_ELEMENT_STYLE_PRESET_TYPES.has(elementType);
  const persistedIndex = persistedPath[0] ?? 0;

  // Calculate internal elements count
  const internalItemsCount = (values?.internal_elements || []).length;
  const hasInternalItems = internalItemsCount > 0;

  // Build hierarchy from groups (excluding MAIN which we treat separately)
  const groupedMetadata = useMemo(() => {
    const copy = {};
    Object.entries(editMetadata || {}).forEach(([k, v]) => {
      if (k === "main_fields") return;
      copy[k] = v;
    });
    return copy;
  }, [editMetadata]);

  const hierarchy = useMemo(() => buildGroupHierarchy(groupedMetadata), [groupedMetadata]);
  const filteredHierarchy = useMemo(() => annotateVisibilityAndFilter(hierarchy, values), [hierarchy, values]);

  // Prepare Basic tab fields
  const basicFields = useMemo(() => {
    const arr = Object.entries(editMetadata?.main_fields || {}).map(([field, metadata]) => ({ field, metadata }));
    return arr;
  }, [editMetadata]);
  const basicVisibleCount = useMemo(() => basicFields.filter(({ metadata }) => shouldShowField(metadata, values)).length, [basicFields, values]);

  // Top-level tabs include "Basic", each root group, and optionally "Internal Elements"
  const topTabs = useMemo(() => {
    const tabs = [];
    if (basicVisibleCount > 0) {
      tabs.push({ key: "__basic__", label: "Basic", type: "basic", count: basicVisibleCount, icon: "star" });
    }
    filteredHierarchy.forEach((n) => {
      tabs.push({ key: n.name, label: n.groupInfo?.title || n.name, type: "group", node: n, count: n.visibleCount, icon: n.groupInfo?.icon });
    });
    // Add Internal Elements tab if items exist
    if (hasInternalItems) {
      tabs.push({ 
        key: "__internal__", 
        label: `Internal Elements (${internalItemsCount})`, 
        type: "internal", 
        count: internalItemsCount, 
        icon: "hierarchy" 
      });
    }
    if (supportsStylePresets) {
      tabs.push({
        key: "__styles__",
        label: "Presets",
        type: "styles",
        icon: "paint-palette",
      });
    }
    
    return tabs;
  }, [filteredHierarchy, basicVisibleCount, hasInternalItems, internalItemsCount, supportsStylePresets]);

  // Single source of truth: EditUIContext persisted path.
  // Avoid local tab state + two-way sync effects (they can ping-pong on elementType changes and cause
  // repeated tab switching / maximum update depth errors).
  const maxIndex = Math.max(topTabs.length - 1, 0);
  const activeTabIndex = Math.min(persistedIndex, maxIndex);

  // If persisted selection is invalid after tabs change, clamp it in the store.
  useEffect(() => {
    if (persistedIndex !== activeTabIndex) {
      setSelectedTabPathForType(elementType, [activeTabIndex]);
    }
  }, [elementType, persistedIndex, activeTabIndex, setSelectedTabPathForType]);

  const handleTabSelect = useCallback(
    (idx) => {
      const next = Math.min(idx, maxIndex);
      setSelectedTabPathForType(elementType, [next]);
    },
    [elementType, maxIndex, setSelectedTabPathForType],
  );

  // Compute content for current selection
  const activeTab = topTabs[activeTabIndex] || topTabs[0];
  let content = null;

  if (!activeTab || activeTab.type === "basic") {
    // Render basic fields
    content = <MainFieldsList fields={basicFields} values={values} onChange={onChange} />;
  } else if (activeTab.type === "internal") {
    // Render Internal Elements list
    content = (
      <InternalElementsList
        elements={values?.internal_elements || []}
        onEdit={onEditInternalItem}
        onDelete={onDeleteInternalItem}
      />
    );
  } else if (activeTab.type === "styles") {
    content = <ElementStylePresetsTab />;
  } else if (activeTab.type === "group") {
    // Render the root group and all its hierarchy as folders
    const rootNode = activeTab.node;
    
    // FIX: If the root has NO children but HAS fields, render those fields as a GroupFolder
    // This ensures fields always show with folder styling (lines, emoji, etc)
    if (!rootNode.children || rootNode.children.length === 0) {
      // No nested groups - just render root fields with folder styling
      if (rootNode.fields && rootNode.fields.length > 0) {
        content = (
          <div className="space-y-4">
            <GroupFolder
              node={rootNode}
              values={values}
              onChange={onChange}
              level={0}
            />
          </div>
        );
      } else {
        // No fields at all - empty content
        content = <div className="p-4 text-gray-500 dark:text-dark-400">No fields in this group</div>;
      }
    } else {
      // Has nested children - render hierarchy
      content = (
        <div className="space-y-4">
          {/* If root has fields, show them first */}
          {rootNode.fields && rootNode.fields.length > 0 && (
            <div className="mb-6">
              <MainFieldsList fields={rootNode.fields} values={values} onChange={onChange} />
            </div>
          )}
          
          {/* Render child groups as folders */}
          <div className="space-y-4">
            {rootNode.children.map((childNode) => (
              <GroupFolder
                key={childNode.name}
                node={childNode}
                values={values}
                onChange={onChange}
                level={0}
              />
            ))}
          </div>
        </div>
      );
    }
  }

  return (
    <div className="h-full flex flex-col">
      {/* Top-level tabs - ALWAYS VISIBLE */}
      <div
        data-tutorial-id="edit-panel-tabs"
        className={`flex-shrink-0 px-3 pt-2 pb-0 ${surfaces.glassCard} sticky top-0 z-20`}
      >
        {isMobileViewport ? (
          <select
            aria-label="Edit section"
            className={`w-full px-3 py-2 border rounded-md shadow-sm ${surfaces.primary} ${textColors.primary} ${typography.bodySmall} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${borders.default}`}
            value={String(activeTabIndex)}
            onChange={(e) => handleTabSelect(Number(e.target.value))}
          >
            {topTabs.map((t, idx) => (
              <option key={t.key} value={String(idx)}>
                {t.label}
              </option>
            ))}
          </select>
        ) : (
          <TabBar tabs={topTabs} activeIndex={activeTabIndex} onSelect={handleTabSelect} />
        )}
      </div>
      
      {/* Content area - scrollable */}
      <div className="flex-1 overflow-y-auto px-3 pt-4 pb-6">
        {content}
      </div>
    </div>
  );
};

export { EditTabsWithFolders, applyFlattenedNestedChange as __test_applyFlattenedNestedChange };

